import { BillingHistoryDocument, DocumentResponse, GetProviderInvoicesResponse, ProviderInvoice, SubscriptionBillingHistoryItem } from "./types";

/** Human-readable label per backend document entityType */
const DOCUMENT_LABELS: Record<string, string> = {
  Invoice: "Invoice",
  Receipt: "Receipt",
  CreditNote: "Credit Note",
  RefundReceipt: "Refund Receipt",
};

const getDocumentLabel = (entityType: string): string =>
  DOCUMENT_LABELS[entityType] ?? entityType.replace(/([a-z])([A-Z])/g, "$1 $2");

/**
 * Map an invoice's documents to downloadable billing-history documents,
 * newest first. Only documents with a usable fileUrl are included.
 */
const toBillingHistoryDocuments = (documents?: DocumentResponse[]): BillingHistoryDocument[] =>
  (documents ?? [])
    .filter((doc) => !!doc.fileUrl)
    .slice()
    .sort((a, b) => new Date(b.generatedAt ?? b.createdAt).getTime() - new Date(a.generatedAt ?? a.createdAt).getTime())
    .map((doc) => ({
      id: doc.documentId,
      label: getDocumentLabel(doc.entityType),
      url: doc.fileUrl,
    }));

/**
 * Maps backend invoice status to a user-friendly display status
 */
const _mapInvoiceStatusToDisplay = (invoice: ProviderInvoice): string => {
  // Completed status means paid for subscriptions
  if (invoice.completedAt || invoice.status === "Completed") {
    return "Paid";
  }
  if (invoice.status === "Active") {
    return "Active";
  }
  if (invoice.status === "PastDue" || invoice.isPastDue) {
    return "Past Due";
  }
  if (invoice.status === "Refunded") {
    return "Refunded";
  }
  if (invoice.status === "Cancelled") {
    return "Cancelled";
  }
  if (invoice.status === "Voided") {
    return "Voided";
  }
  if (invoice.status === "Finalized") {
    return "Pending";
  }
  return invoice.status;
};

/**
 * Transforms a ProviderInvoice to a SubscriptionBillingHistoryItem
 * Note: planName defaults to "Subscription" - to get the actual plan name,
 * the subscription data needs to be fetched separately using metadata.subscriptionPlanId
 */
export const transformInvoiceToBillingHistoryItem = (
  invoice: ProviderInvoice,
  planNameOverride?: string
): SubscriptionBillingHistoryItem => {
  // Surface ALL documents (Invoice, Receipt, Credit Note, Refund Receipt),
  // newest first. The latest is the default download.
  const documents = toBillingHistoryDocuments(invoice.documents);

  return {
    id: invoice.salesOrderId,
    // Use plan name override if provided, otherwise use a default
    // The actual plan name can be looked up using metadata.subscriptionPlanId
    planName: planNameOverride || "Subscription Plan",
    // Use finalizedAt as the invoice date, fall back to createdAt
    date: invoice.finalizedAt || invoice.createdAt,
    // Keep amount in cents - formatCurrency utility handles conversion to dollars
    amount: invoice.orderTotal,
    currency: invoice.currency,
    status: invoice.status,
    documents,
    // Deprecated: latest document's URL, kept for backwards compatibility
    documentUrl: documents[0]?.url,
  };
};

/**
 * Transforms the full provider invoices response to billing history items
 */
export const transformProviderInvoicesToBillingHistory = (response: GetProviderInvoicesResponse): SubscriptionBillingHistoryItem[] => {
  return response.data.map((invoice) => {
    const planName = invoice.metadata?.notes ?? undefined;
    return transformInvoiceToBillingHistoryItem(invoice, planName);
  });
};
